import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTracking, useTransactions, useUI } from '../hooks';
import Text from '../components/Text/Text';
import LabeledValue from '../components/LabeledValue';
import packageImage from '../images/package.png'; // Assuming you have a package image in your assets

const TrackingStatusScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getTrackingInfo, trackingInfo, isLoading: trackingLoading, error: trackingError } = useTracking();
  const { releasePayment } = useTransactions();
  const { showToast } = useUI();
  
  // Fetch tracking info on component mount
  useEffect(() => {
    const fetchTrackingInfo = async () => {
      if (!id) return;
      
      try {
        await getTrackingInfo(id);
      } catch (error) {
        showToast('error', 'Failed to load tracking information');
      }
    };
    
    fetchTrackingInfo();
  }, [id, getTrackingInfo, showToast]);
  
  // Handle contact seller click
  // const handleContactSellerClick = () => {
  //   showToast('info', 'Contact seller feature is not implemented in this demo');
  // };
  
  // Handle refresh click
  const handleRefreshClick = () => {
    if (!id) return;
    
    getTrackingInfo(id)
      .then(() => showToast('success', 'Tracking information refreshed'))
      .catch(() => showToast('error', 'Failed to refresh tracking information'));
  };
  
  // Render loading state
  if (trackingLoading) {
    return (
      <div className="tracking-status-container">
        <div className="loading-state">
          <Text>Loading tracking information...</Text>
        </div>
      </div>
    );
  }
  
  // Render error state
  if (trackingError || !id || !trackingInfo[id]) {
    return (
      <div className="tracking-status-container">
        <div className="error-state">
          <Text className="text-error">Failed to load tracking information</Text>
          <button 
            className="retry-button"
            onClick={() => id && getTrackingInfo(id)}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  const tracking = trackingInfo[id];
  
  // Determine status text and color
  let statusText = '';
  let statusColor = '';
  
  if (tracking.status === 'not_provided') {
    statusText = 'Not Provided';
    statusColor = 'text-gray';
  } else if (tracking.status === 'pending') {
    statusText = 'Pending';
    statusColor = 'text-warning';
  } else if (tracking.status === 'en_route') {
    statusText = 'In Transit';
    statusColor = 'text-primary';
  } else if (tracking.status === 'delivered') {
    statusText = 'Delivered';
    statusColor = 'text-success';
  } else if (tracking.status === 'awaiting_collection') {
    statusText = 'Awaiting Collection';
    statusColor = 'text-info';
  } else if (tracking.status === 'collected') {
    statusText = 'Collected';
    statusColor = 'text-success';
  } else if (tracking.status === 'cancelled') {
    statusText = 'Cancelled';
    statusColor = 'text-danger';
  }
  
  return (
    <div className="tracking-status-container">
      <div className="tracking-status-header">
        <div className="package-image">
          <img 
            src={packageImage} 
            alt="Package" 
          />
          <div className="status-badge">
            <Text className={`text-body ${statusColor}`}>{statusText}</Text>
          </div>
        </div>
      </div>
      
      <div className="package-info">
        <Text className="text-h2">Shipping Detail</Text>
        <div className="tracking-details">
          <LabeledValue label='Carrier' value={tracking.carrier} rightAligned={true} />
          <LabeledValue label='Tracking #' value={tracking.trackingNumber} rightAligned={true} />
          {tracking.estimatedDeliveryDate && (
            <LabeledValue label='Estimated Delivery' value={new Date(tracking.estimatedDeliveryDate).toLocaleDateString()} />
          )}
          {tracking.actualDeliveryDate && (
            <LabeledValue label='Delivered On' value={new Date(tracking.actualDeliveryDate).toLocaleDateString()} />
          )}
        </div>
      </div>
      
      {/* <div className="delivery-address">
        <Text className="text-h2">Delivery Details</Text>
        <div className="tracking-details">
          <LabeledValue label='Recipient' value={tracking.deliveryAddress.recipient} />
          <LabeledValue label='Delivery Address' multiline={true} 
            value={`${tracking.deliveryAddress.street}, ${tracking.deliveryAddress.city}, ${tracking.deliveryAddress.state} ${tracking.deliveryAddress.postalCode}, ${tracking.deliveryAddress.country}`} />
        </div>
      </div> */}
      
      {/* <div className="package-actions"> */}
        {/* <button 
          className="action-button contact-seller"
          onClick={handleContactSellerClick}
        >
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" fill="none">
            <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <Text className="text-caption">Contact seller</Text>
        </button> */}
        
        {/* <button 
          className="action-button refresh"
          onClick={handleRefreshClick}
        >
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" fill="none">
            <path d="M23 4v6h-6M1 20v-6h6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <Text className="text-caption">Refresh</Text>
        </button> */}
        
      {/* </div> */}
      
      <div className="tracking-history">
        <Text className="text-h2">Tracking History</Text>
        <div className="history-timeline">
          {tracking.statusHistory.map((historyItem, index) => (
            <div key={index} className="history-item">
              <div className="history-marker"></div>
              <div className="history-content">
                <div>
                  <Text className="text-body"><strong>{historyItem.status}</strong></Text>
                </div>
                <Text className="text-caption">
                  {new Date(historyItem.timestamp).toLocaleString()}
                </Text>
                <div>
                  {historyItem.location && (
                    <Text className="text-caption">{historyItem.location}</Text>
                  )}
                  {historyItem.notes && (
                    <Text className="text-caption">{historyItem.notes}</Text>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TrackingStatusScreen;